// Local autosave persistence — the offline/crash fallback layer beneath the backend
// autosave. Stored values are wrapped with a savedAt timestamp so recovery can pick
// the newest of {local, backend draft, live post}. See the design spec.

export interface DraftSnapshot {
  title: string;
  featured_image_url: string | null;
  body_html: string;
  published: boolean;
}

export interface StoredDraft {
  snapshot: DraftSnapshot;
  savedAt: number; // epoch ms
}

const PREFIX = 'nd_admin_draft_';

// All new posts intentionally share the single `nd_admin_draft_new` key until the
// backend assigns an id, at which point migrateDraftKey moves it to the id key.
export function draftKey(id: number | 'new'): string {
  return `${PREFIX}${id}`;
}

export function persistDraftLocal(key: string, snapshot: DraftSnapshot): void {
  try {
    const stored: StoredDraft = { snapshot, savedAt: Date.now() };
    localStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // storage unavailable (private mode / quota) — autosave silently degrades
  }
}

export function loadStoredDraft(key: string): StoredDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // New format: { snapshot, savedAt }. Old format: a bare snapshot — treat as oldest.
    if (parsed && typeof parsed === 'object' && 'snapshot' in parsed) {
      return parsed as StoredDraft;
    }
    return { snapshot: parsed as DraftSnapshot, savedAt: 0 };
  } catch {
    return null;
  }
}

export function clearDraftLocal(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// Move the shared `new` draft to its real id key once the backend creates the row.
export function migrateDraftKey(from: number | 'new', to: number): void {
  const stored = loadStoredDraft(draftKey(from));
  if (!stored) return;
  try {
    localStorage.setItem(draftKey(to), JSON.stringify(stored));
  } catch {
    // ignore
  }
  clearDraftLocal(draftKey(from));
}
